import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { userService, CreateUserData, UpdateUserData } from '@/services/userService';
import { projectService } from '@/services/projectService';
import { projectMembershipService } from '@/services/projectMembershipService';
import { User, Project } from '@/types';
import { formatDate } from '@/utils';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';

const baseUserSchema = {
  email: z.string().email('Email invalide'),
  fullName: z.string().min(6, 'Le nom complet doit contenir au moins 6 caractères'),
  roles: z.array(z.string()),
  phone: z.string().optional(),
  isActive: z.boolean().default(true),
  project: z.string().optional(),
  projectRole: z.enum(['PROJECT_ADMIN', 'PROJECT_MANAGER', 'PROJECT_USER']).optional(),
};

const createUserSchema = z.object({
  ...baseUserSchema,
  password: z.string().min(9, 'Le mot de passe doit contenir au moins 9 caractères'),
}).refine((data) => {
  const isRoot = data.roles.includes('ROLE_ROOT');
  if (!isRoot) {
    return !!data.project && !!data.projectRole;
  }
  return true;
}, {
  message: 'Le projet et le rôle projet sont requis pour les utilisateurs non-ROOT',
  path: ['project'],
});

const updateUserSchema = z.object({
  ...baseUserSchema,
  password: z.string().optional(), // Password is optional for updates, validation handled in register()
}).refine((data) => {
  const isRoot = data.roles.includes('ROLE_ROOT');
  if (!isRoot) {
    return !!data.project && !!data.projectRole;
  }
  return true;
}, {
  message: 'Le projet et le rôle projet sont requis pour les utilisateurs non-ROOT',
  path: ['project'],
});

type UserFormData = z.infer<typeof createUserSchema> | z.infer<typeof updateUserSchema>;

const ROLES = ['ROLE_ROOT', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_USER'];

// Password strength calculator
const calculatePasswordStrength = (password: string): { strength: 'weak' | 'medium' | 'strong'; score: number; color: string } => {
  if (!password || password.length === 0) {
    return { strength: 'weak', score: 0, color: 'bg-red-500' };
  }

  let score = 0;
  
  // Length check (min 9 characters)
  if (password.length >= 9) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 15) score += 1;
  
  // Lowercase check
  if (/[a-z]/.test(password)) score += 1;
  
  // Uppercase check
  if (/[A-Z]/.test(password)) score += 1;
  
  // Digit check
  if (/\d/.test(password)) score += 1;
  
  // Special character check
  if (/[@$!#"%*?&]/.test(password)) score += 1;
  
  // Determine strength
  if (score <= 2) {
    return { strength: 'weak', score, color: 'bg-red-500' };
  } else if (score <= 4) {
    return { strength: 'medium', score, color: 'bg-yellow-500' };
  } else {
    return { strength: 'strong', score, color: 'bg-green-500' };
  }
};

export function UsersManagementPage() {
  const { user, isRoot, isAdmin } = useAuthStore();
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const itemsPerPage = 20;

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<UserFormData>({
    resolver: zodResolver(editingUser ? updateUserSchema : createUserSchema),
    defaultValues: {
      roles: [],
      isActive: false, // Default to false
      password: '',
    },
    mode: 'onChange',
  });

  const watchedRoles = watch('roles');
  const watchedProject = watch('project');
  const isRootUser = watchedRoles?.includes('ROLE_ROOT') || false;

  // Fetch users
  const {
    data: paginatedResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', currentPage, searchQuery],
    queryFn: () => userService.getUsers({ page: currentPage, itemsPerPage, search: searchQuery }),
    enabled: isRoot() || isAdmin(),
  });

  // Removed: No longer needed - ProjectSearchSelect handles its own queries

  // Fetch user's current membership when editing
  const { data: userMemberships } = useQuery({
    queryKey: ['userMemberships', editingUser?.id],
    queryFn: () => {
      if (!editingUser?.id) return Promise.resolve([]);
      return projectMembershipService.getMembershipsByUser(editingUser.id);
    },
    enabled: !!editingUser?.id,
  });

  const users = paginatedResponse?.data || [];
  const totalItems = paginatedResponse?.totalItems || 0;
  const totalPages = paginatedResponse?.totalPages || 0;

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateUserData) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
        setToastMessage('Utilisateur créé avec succès !');
        setToastType('success');
        setShowToast(true);
        setIsModalOpen(false);
        setShowPassword(false);
        reset();
    },
    onError: () => {
      setToastMessage('Erreur lors de la création de l\'utilisateur');
      setToastType('error');
      setShowToast(true);
    },
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserData }) => userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
        setToastMessage('Utilisateur modifié avec succès !');
        setToastType('success');
        setShowToast(true);
        setIsModalOpen(false);
        setShowPassword(false);
        setEditingUser(null);
        reset();
    },
    onError: () => {
      setToastMessage('Erreur lors de la modification de l\'utilisateur');
      setToastType('error');
      setShowToast(true);
    },
  });


  const onSubmit = async (data: UserFormData) => {
    if (editingUser) {
      const updateData: UpdateUserData = {
        email: data.email,
        fullName: data.fullName,
        roles: data.roles,
        phone: data.phone,
        isActive: data.isActive,
      };

      // Only include password if it's provided (not empty)
      if (data.password && data.password.trim() !== '') {
        updateData.password = data.password;
      }
      if (data.password) {
        updateData.password = data.password;
      }
      
      // Update user
      await updateMutation.mutateAsync({ id: editingUser.id, data: updateData });

      // Reload memberships fresh from API before updating
      const freshMemberships = await projectMembershipService.getMembershipsByUser(editingUser.id);
      
      // Handle project membership update
      if (!isRootUser && data.project && data.projectRole) {
        const targetProjectId = parseInt(data.project.match(/\/(\d+)$/)?.[1] || '0', 10);
        const membershipForProject = (freshMemberships || []).find((m) => {
          const projectId =
            typeof m.project === 'object' && m.project !== null
              ? m.project.id
              : typeof m.project === 'string'
                ? parseInt(m.project.match(/\/(\d+)$/)?.[1] || '0', 10)
                : 0;
          return projectId === targetProjectId;
        });

        if (membershipForProject) {
          // Update role for this project (do NOT touch other memberships)
          await projectMembershipService.updateMembership(membershipForProject.id, {
            user: `/api/users/${editingUser.id}`,
            project: data.project,
            role: data.projectRole,
          });
        } else {
          // Create an additional membership for this project
          await projectMembershipService.createMembership({
            user: `/api/users/${editingUser.id}`,
            project: data.project,
            role: data.projectRole,
          });
        }
      }
      
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['userMemberships'] });
      await queryClient.invalidateQueries({ queryKey: ['projectMemberships'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      setShowPassword(false);
      reset();
      setEditingUser(null);
    } else {
      if (!data.password) {
        alert('Le mot de passe est requis pour créer un utilisateur');
        return;
      }
      
      // Ensure non-ROOT users have ROLE_USER
      const roles = data.roles.includes('ROLE_ROOT') ? ['ROLE_ROOT'] : ['ROLE_USER'];
      
      // Create user
      const newUser = await createMutation.mutateAsync({
        email: data.email,
        fullName: data.fullName,
        password: data.password,
        roles: roles,
        phone: data.phone,
        isActive: data.isActive,
      });

      // Create project membership if project is selected and user is not ROOT
      if (!isRootUser && data.project && data.projectRole) {
        await projectMembershipService.createMembership({
          user: `/api/users/${newUser.id}`,
          project: data.project,
          role: data.projectRole,
        });
        queryClient.invalidateQueries({ queryKey: ['projectMemberships'] });
      }
      
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      reset();
      setEditingUser(null);
      setShowPassword(false);
    }
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    setShowPassword(false);
    
    // Convert isActive to boolean explicitly
    // Handle: true, 1, "1", "true" -> true
    // Handle: false, 0, "0", "false", null, undefined -> false
    let isActiveValue: boolean;
    if (user.isActive === true || user.isActive === 1 || user.isActive === '1' || user.isActive === 'true') {
      isActiveValue = true;
    } else {
      // Default to false for: false, 0, "0", "false", null, undefined, etc.
      isActiveValue = false;
    }
    
    // Load user's membership first
    const memberships = await projectMembershipService.getMembershipsByUser(user.id);
    let projectValue = '';
    let projectRoleValue: 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER' | undefined = undefined;
    
    if (memberships.length > 0) {
      const membership = memberships[0];
      // Handle both object and IRI string formats
      let projectId: number | null = null;
      
      if (typeof membership.project === 'object' && membership.project !== null) {
        projectId = membership.project.id;
      } else if (typeof membership.project === 'string') {
        const match = membership.project.match(/\/(\d+)$/);
        projectId = match ? parseInt(match[1], 10) : null;
      }
      
      if (projectId) {
        projectValue = `/api/projects/${projectId}`;
        // Pre-fetch the project so ProjectSearchSelect can display it
        try {
          await queryClient.prefetchQuery({
            queryKey: ['project', projectId],
            queryFn: () => projectService.getProject(projectId!),
          });
        } catch (error) {
          console.error('Failed to prefetch project:', error);
        }
      }
      
      if (membership.role) {
        projectRoleValue = membership.role as 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER';
      }
    }
    
    // Reset form with all values including isActive
    reset({
      email: user.email,
      fullName: user.fullName,
      roles: user.roles || [],
      phone: user.phone || '',
      isActive: isActiveValue,
      password: '',
      project: projectValue,
      projectRole: projectRoleValue,
    }, {
      keepDefaultValues: false,
    });
    
    setIsModalOpen(true);
    
    // Force update isActive after modal is opened to ensure it's properly set
    setTimeout(() => {
      setValue('isActive', isActiveValue, { shouldValidate: false, shouldDirty: false });
    }, 100);
  };


  const handleNewUser = () => {
    setEditingUser(null);
    setShowPassword(false);
    reset({
      roles: ['ROLE_USER'], // Default to "Utilisateur projet"
      isActive: true,
      project: '',
      projectRole: undefined,
    });
    setIsModalOpen(true);
  };

  const toggleRole = (role: string) => {
    const currentRoles = watchedRoles || [];
    if (currentRoles.includes(role)) {
      setValue('roles', currentRoles.filter((r) => r !== role));
    } else {
      setValue('roles', [...currentRoles, role]);
    }
  };

  // Update isRootUser when roles change and clear project if user becomes ROOT
  // Only clear if we're creating a new user (not editing)
  useEffect(() => {
    if (!editingUser) {
      const currentRoles = watchedRoles || [];
      const isRoot = currentRoles.includes('ROLE_ROOT');
      if (isRoot) {
        // Clear project selection if user becomes ROOT
        setValue('project', '');
        setValue('projectRole', undefined);
      }
    }
  }, [watchedRoles, setValue, editingUser]);

  if (!isRoot() && !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Accès refusé. Vous devez être administrateur.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Utilisateurs
          </div>
          <div className="flex items-center gap-4">
            <div className="relative hidden sm:block">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-icons-outlined text-slate-400 text-lg">search</span>
              </span>
              <input
                className="pl-10 pr-4 py-2 w-64 text-sm bg-slate-50 dark:bg-[#0B1116] border border-slate-200 dark:border-[#1E293B] rounded-full focus:outline-none focus:ring-2 focus:ring-primary dark:text-white placeholder-slate-400"
                placeholder="Rechercher..."
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
            <button className="relative p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors">
              <span className="material-icons-outlined">notifications</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Gestion des Utilisateurs</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Gérez l'ensemble des utilisateurs et leurs permissions.
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex gap-3">
              <button
                onClick={handleNewUser}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                <span className="material-icons-outlined text-lg mr-2">add</span>
                Nouvel Utilisateur
              </button>
            </div>
          </div>

          {/* Users Table */}
          {isLoading ? (
            <div className="text-center py-12">Chargement...</div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">Erreur lors du chargement des utilisateurs</div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Aucun utilisateur trouvé</div>
          ) : (
            <>
              <div className="bg-white dark:bg-[#16202A] rounded-lg border border-slate-200 dark:border-[#1E293B] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-[#1E293B]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Nom complet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Rôles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Créé le
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-[#1E293B]">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{user.fullName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-wrap gap-1">
                            {user.roles?.map((role) => (
                              <span
                                key={role}
                                className="px-2 py-1 text-xs rounded bg-primary/10 text-primary"
                              >
                                {role}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              user.isActive
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}
                          >
                            {user.isActive ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {/* Admin cannot edit super admins (ROLE_ROOT) */}
                            {!(isAdmin() && !isRoot() && (user.roles?.includes('ROLE_ROOT') || user.roles?.includes('ROLE_SUPER_ROOT'))) && (
                              <button
                                onClick={() => handleEdit(user)}
                                className="text-primary hover:text-primary/80"
                                title="Modifier"
                              >
                                <span className="material-icons-outlined">edit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-slate-500">
                    Affichage de {(currentPage - 1) * itemsPerPage + 1} à{' '}
                    {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems} utilisateurs
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-[#1E293B]"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-[#1E293B]"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
              setShowPassword(false);
              reset();
              setEditingUser(null);
            }
          }}
        >
          <div
            className="bg-white dark:bg-[#16202A] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setShowPassword(false);
                  reset();
                  setEditingUser(null);
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom complet *</label>
                <input
                  {...register('fullName')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Mot de passe {editingUser ? '(laisser vide pour ne pas changer)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      validate: (value) => {
                        // For new users, password is required
                        if (!editingUser) {
                          if (!value || value.trim() === '') {
                            return 'Le mot de passe est requis';
                          }
                          if (value.length < 9) {
                            return 'Le mot de passe doit contenir au moins 9 caractères';
                          }
                          // Check password pattern: at least one lowercase, one uppercase, one digit, one special char
                          const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!#"%*?&])[A-Za-z\d@$!#"%*?&]{9,}$/;
                          if (!passwordPattern.test(value)) {
                            return 'Le mot de passe doit contenir au moins une minuscule, une majuscule, un chiffre et un caractère spécial (@$!#"%*?&)';
                          }
                        }
                        // For existing users, password is optional, but if provided, it must meet requirements
                        if (editingUser && value && value.trim() !== '') {
                          if (value.length < 9) {
                            return 'Le mot de passe doit contenir au moins 9 caractères';
                          }
                          const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!#"%*?&])[A-Za-z\d@$!#"%*?&]{9,}$/;
                          if (!passwordPattern.test(value)) {
                            return 'Le mot de passe doit contenir au moins une minuscule, une majuscule, un chiffre et un caractère spécial (@$!#"%*?&)';
                          }
                        }
                        return true;
                      },
                    })}
                    className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-[#94A3B8] hover:text-slate-600 dark:hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    <span className="material-icons-outlined text-lg">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                
                {/* Password requirements description */}
                {!editingUser && (
                  <div className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                    <p className="mb-1">Le mot de passe doit contenir :</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li>Minimum 9 caractères</li>
                      <li>Au moins une lettre minuscule (a-z)</li>
                      <li>Au moins une lettre majuscule (A-Z)</li>
                      <li>Au moins un chiffre (0-9)</li>
                      <li>Au moins un caractère spécial (@$!#"%*?&)</li>
                    </ul>
                  </div>
                )}
                
                {/* Password strength indicator */}
                {watch('password') && watch('password')!.length > 0 && (
                  <div className="mt-2">
                    {/* Format explanation text */}
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                      Format requis : minimum 9 caractères avec au moins une lettre minuscule, une lettre majuscule, un chiffre et un caractère spécial (@$!#"%*?&)
                    </p>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${calculatePasswordStrength(watch('password') || '').color}`}
                          style={{
                            width: `${Math.min((calculatePasswordStrength(watch('password') || '').score / 7) * 100, 100)}%`
                          }}
                        />
                      </div>
                      <span className={`text-xs font-semibold whitespace-nowrap ${
                        calculatePasswordStrength(watch('password') || '').strength === 'weak' ? 'text-red-500' :
                        calculatePasswordStrength(watch('password') || '').strength === 'medium' ? 'text-yellow-500' :
                        'text-green-500'
                      }`}>
                        {calculatePasswordStrength(watch('password') || '').strength === 'weak' ? 'Faible' :
                         calculatePasswordStrength(watch('password') || '').strength === 'medium' ? 'Moyen' :
                         'Fort'}
                      </span>
                    </div>
                  </div>
                )}
                
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <input
                  {...register('phone')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Type d'utilisateur *</label>
                <div className="flex flex-wrap gap-2">
                  {/* Only super admins can assign ROLE_ROOT */}
                  {isRoot() && (
                    <button
                      type="button"
                      onClick={() => {
                        setValue('roles', ['ROLE_ROOT']);
                        setValue('project', '');
                        setValue('projectRole', undefined);
                      }}
                      className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                        watchedRoles?.includes('ROLE_ROOT')
                          ? 'bg-primary text-white'
                          : 'bg-slate-100 dark:bg-[#1E293B] text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-[#1E293B]'
                      }`}
                    >
                      Administrateur système (ROOT)
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setValue('roles', ['ROLE_USER']);
                      // Auto-select PROJECT_USER role when "Utilisateur projet" is selected
                      if (watch('project') && !watch('projectRole')) {
                        setValue('projectRole', 'PROJECT_USER');
                      }
                    }}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      watchedRoles?.includes('ROLE_USER') && !watchedRoles?.includes('ROLE_ROOT')
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 dark:bg-[#1E293B] text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-[#1E293B]'
                    }`}
                  >
                    Utilisateur projet
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {watchedRoles?.includes('ROLE_ROOT')
                    ? 'Accès complet à tous les projets'
                    : 'Accès limité à un projet spécifique'}
                </p>
                {isAdmin() && !isRoot() && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Vous ne pouvez pas modifier les super admins (ROOT).
                  </p>
                )}
                {errors.roles && <p className="text-red-500 text-sm mt-1">{errors.roles.message}</p>}
              </div>

              <div>
                <Controller
                  name="isActive"
                  control={control}
                  key={editingUser?.id || 'new'} // Force re-render when switching users
                  render={({ field }) => {
                    // Convert field.value to boolean explicitly - only true, 1, "1", "true" should be checked
                    // Everything else (false, 0, "0", "false", null, undefined) should be unchecked
                    const checkedValue = field.value === true || field.value === 1 || field.value === '1' || field.value === 'true';
                    
                    return (
                      <label className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          checked={checkedValue}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                        />
                        <span className="text-sm font-medium">Utilisateur actif</span>
                      </label>
                    );
                  }}
                />
              </div>

              {/* Project selection - only show if user is not ROOT */}
              {!isRootUser && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Projet *</label>
                    <Controller
                      name="project"
                      control={control}
                      render={({ field }) => (
                        <ProjectSearchSelect
                          value={field.value || ''}
                          onChange={(value) => {
                            field.onChange(value);
                            // Auto-select PROJECT_USER when a project is selected
                            if (value && !watch('projectRole')) {
                              setValue('projectRole', 'PROJECT_USER');
                            }
                          }}
                          onBlur={field.onBlur}
                          error={errors.project?.message}
                        />
                      )}
                    />
                  </div>

                  {watchedProject && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Rôle dans le projet *</label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'PROJECT_ADMIN', label: 'Administrateur' },
                          { value: 'PROJECT_MANAGER', label: 'Manager' },
                          { value: 'PROJECT_USER', label: 'Utilisateur' },
                        ].map((role) => (
                          <button
                            key={role.value}
                            type="button"
                            onClick={() => setValue('projectRole', role.value as 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER')}
                            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                              watch('projectRole') === role.value
                                ? 'bg-primary text-white'
                                : 'bg-slate-100 dark:bg-[#1E293B] text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-[#1E293B]'
                            }`}
                          >
                            {role.label}
                          </button>
                        ))}
                      </div>
                      {errors.projectRole && <p className="text-red-500 text-sm mt-1">{errors.projectRole.message}</p>}
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setShowPassword(false);
                    reset();
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg hover:bg-slate-50 dark:hover:bg-[#1E293B]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {editingUser ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
